import { formatMinorUnits } from './money';
import { ApiError, fetchWithSession, resolveBaseUrl } from './client';
import type { components } from './schema';
import type { AiStructuredResponseData } from '@/ui';

export type ProposalSignature = components['schemas']['ProposalSignatureView'];
export type Proposal = components['schemas']['ProposalView'];
export type ProposalVersion = components['schemas']['ProposalVersionView'];
export type Options = components['schemas']['ProposalOptions'];
export type PriceOption = components['schemas']['ProposalPriceOption'];
export type LineInput = components['schemas']['ProposalLineInput'];
export type LineView = components['schemas']['ProposalLineView'];
export type OptionTotal = components['schemas']['ProposalOptionTotal'];
export type Finding = components['schemas']['ProposalFinding'];
export type ExceptionRequest = components['schemas']['PriceExceptionView'];
export type ExceptionSet = components['schemas']['PriceExceptionSet'];
export type ExceptionOption = components['schemas']['PriceExceptionOption'];
export type Template = components['schemas']['ProposalTemplateView'];
export type TemplateInput = components['schemas']['ProposalTemplateInput'];
export type TemplateBlockInput = components['schemas']['ProposalTemplateBlockInput'];
export type LinkView = components['schemas']['ProposalLinkView'];
export type Term = components['schemas']['ProposalTerm'];
export type Diff = components['schemas']['ProposalVersionDiff'];
export type LinkIssued = components['schemas']['ProposalLinkIssued'];
export type PresentedWithLinkView = components['schemas']['ProposalPresentedWithLink'];
export type CustomerContact = components['schemas']['ProposalCustomerContact'];
export type PublicProposal = components['schemas']['PublicProposalView'];
export type PublicReceipt = components['schemas']['PublicProposalReceipt'];
export type OptionTier = 'GOOD' | 'BETTER' | 'BEST';

/** How many proposals the directory asks for at a time. */
export const PROPOSAL_PAGE_SIZE = 25;

/** The header the server counts every matching proposal in. */
const TOTAL_COUNT_HEADER = 'X-Total-Count';

async function exchange(
  url: string,
  method: string,
  body?: unknown,
  authenticated = true,
): Promise<Response> {
  const request = new Request(url, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const response = authenticated ? await fetchWithSession(request) : await fetch(request);

  if (!response.ok) {
    const error: unknown = await response.json().catch(() => null);
    const detail =
      error !== null && typeof error === 'object' && 'detail' in error ? error.detail : undefined;
    throw new ApiError(
      response.status,
      typeof detail === 'string' && detail.trim() ? detail : 'Proposal request failed',
    );
  }
  return response;
}

async function send<T>(url: string, method: string, body?: unknown, authenticated = true) {
  const response = await exchange(url, method, body, authenticated);
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Calls the workspace proposal API with the signed-in session.
 *
 * @param path the path beneath /api/proposals
 * @param method the HTTP method
 * @param body the request body, when there is one
 * @returns the parsed response
 */
export function proposalRequest<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  return send<T>(resolveBaseUrl() + '/api/proposals' + path, method, body);
}

/** One page of the proposal directory and how many proposals match in all. */
export interface ProposalPage {
  readonly rows: Proposal[];
  readonly total: number;
}

/**
 * Reads one page of the proposals the caller may see.
 *
 * @param page zero-based page number
 * @param size how many proposals per page
 * @param opportunityId narrows the page to one opportunity's proposals when given
 * @returns the page, with the total the server counted
 */
export async function listProposals(
  page: number,
  size: number = PROPOSAL_PAGE_SIZE,
  opportunityId?: string,
): Promise<ProposalPage> {
  const query = new URLSearchParams({ page: String(page), size: String(size) });
  if (opportunityId !== undefined && opportunityId !== '') {
    query.set('opportunityId', opportunityId);
  }
  const response = await exchange(`${resolveBaseUrl()}/api/proposals?${query.toString()}`, 'GET');
  const rows = (await response.json()) as Proposal[];
  const header = response.headers.get(TOTAL_COUNT_HEADER);
  const counted = header === null ? Number.NaN : Number(header);
  // An older server sends no header; what has been seen so far is then the best lower bound.
  const total = Number.isSafeInteger(counted) && counted >= 0 ? counted : page * size + rows.length;
  return { rows, total };
}

/**
 * Asks the assistant to explain what the pricing controls found on a proposal.
 *
 * @param proposalId the proposal
 * @returns the assistant's structured reading of the findings
 */
export function explainFindings(proposalId: string): Promise<AiStructuredResponseData> {
  return proposalRequest('/' + encodeURIComponent(proposalId) + '/findings/explanation', 'POST');
}

/**
 * Calls the customer approval link, which carries no session at all.
 *
 * Deliberately not routed through {@link fetchWithSession}: that attaches the
 * signed-in agent's bearer token and retries through a refresh, and a customer
 * link must behave identically whether or not somebody happens to be signed in
 * on the same browser. The server refuses a token-scoped decision made by a
 * workspace user, so sending one would turn a shared laptop into a 400.
 *
 * @param slug the workspace slug from the URL
 * @param token the opaque token from the URL
 * @param method the HTTP method
 * @param path the path beneath the token
 * @param body the request body, when there is one
 * @returns the parsed response
 */
export function publicProposalRequest<T>(
  slug: string,
  token: string,
  method = 'GET',
  path = '',
  body?: unknown,
): Promise<T> {
  return send<T>(
    `${resolveBaseUrl()}/api/public/proposals/${encodeURIComponent(slug)}/${encodeURIComponent(token)}${path}`,
    method,
    body,
    false,
  );
}

/**
 * Money, for display, from integer minor units.
 *
 * @param minorUnits the amount in minor units
 * @param currency the ISO currency code
 * @returns the formatted amount
 */
export function money(minorUnits: number, currency: string): string {
  return formatMinorUnits(minorUnits, currency);
}
